import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, Braces, Check, Eye, Gift, Globe, ImagePlus, Info, MessageSquare, Sparkles, Star, Wrench, Zap } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/lib/model-utils";
import { cn } from "@/lib/utils";
import { getProviderIcon } from "@llmgateway/shared/components";
import type { ApiModel, ApiModelProviderMapping, ApiProvider } from "@/lib/fetch-models";
import type { TranslationValues } from "@llmgateway/i18n";
import type { LucideProps } from "lucide-react";
import type { RowComponentProps } from "react-window";
export type ModelSelectorTranslate = (key: string, values?: TranslationValues) => string;
export interface FilterState {
  providers: string[];
  capabilities: string[];
  priceRange: "free" | "low" | "medium" | "high" | "all";
  hideUnstable: boolean;
  showOnlyRoot: boolean;
  showFavoritesOnly: boolean;
  showOnlyWithKeys: boolean;
}

// helper to extract simple capability labels from a mapping
export function getMappingCapabilities(mapping?: ApiModelProviderMapping, model?: ApiModel): string[] {
  const labels: string[] = [];
  if (mapping) {
    if (mapping.streaming) {
      labels.push("Streaming");
    }
    if (mapping.vision) {
      labels.push("Vision");
    }
    if (mapping.tools) {
      labels.push("Tools");
    }
    if (mapping.reasoning) {
      labels.push("Reasoning");
    }
    if (mapping.webSearch) {
      labels.push("Web Search");
    }
  }

  // Image Generation capability if model outputs include images
  if (model?.output?.includes("image")) {
    labels.push("Image Generation");
  }
  return labels;
}
export function getCapabilityIconConfig(capability: string): {
  Icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>> | null;
  color: string;
} {
  switch (capability) {
    case "Streaming":
      return {
        Icon: Zap,
        color: "text-blue-500"
      };
    case "Vision":
      return {
        Icon: Eye,
        color: "text-green-500"
      };
    case "Tools":
      return {
        Icon: Wrench,
        color: "text-purple-500"
      };
    case "Reasoning":
      return {
        Icon: MessageSquare,
        color: "text-orange-500"
      };
    case "JSON Output":
      return {
        Icon: Braces,
        color: "text-cyan-500"
      };
    case "Image Generation":
      return {
        Icon: ImagePlus,
        color: "text-pink-500"
      };
    case "Web Search":
      return {
        Icon: Globe,
        color: "text-teal-500"
      };
    default:
      return {
        Icon: null,
        color: ""
      };
  }
}
export function getCapabilityLabel(capability: string, t: ModelSelectorTranslate): string {
  switch (capability) {
    case "Streaming":
      return t("modelSelector.capabilityStreaming");
    case "Vision":
      return t("modelSelector.capabilityVision");
    case "Tools":
      return t("modelSelector.capabilityTools");
    case "Reasoning":
      return t("modelSelector.capabilityReasoning");
    case "Image Generation":
      return t("modelSelector.capabilityImageGeneration");
    case "Web Search":
      return t("modelSelector.capabilityWebSearch");
    case "JSON Output":
      return t("modelSelector.capabilityJsonOutput");
    default:
      return capability;
  }
}

// helper to check if a model is unstable or experimental
export function isModelUnstable(mapping: ApiModelProviderMapping, model: ApiModel): boolean {
  return [mapping.stability, model.stability].some(stability => stability === "unstable" || stability === "experimental");
}
type PriceField = "input" | "output" | "cachedInput" | "request" | "imageInput" | "imageOutput";
export interface MappingPriceInfo {
  label: string;
  original?: string;
  discounted?: string;
}

// Helper to format prices using any provider discount while reusing shared formatPrice logic.
export function getMappingPriceInfo(mapping: ApiModelProviderMapping | undefined, field: PriceField): MappingPriceInfo {
  if (!mapping) {
    return {
      label: "N/A"
    };
  }
  let basePriceStr: string | null | undefined;
  if (field === "input") {
    basePriceStr = mapping.inputPrice;
  } else if (field === "output") {
    basePriceStr = mapping.outputPrice;
  } else if (field === "cachedInput") {
    basePriceStr = mapping.cachedInputPrice;
  } else if (field === "request") {
    basePriceStr = mapping.requestPrice;
  } else if (field === "imageInput") {
    basePriceStr = mapping.imageInputPrice;
  }
  if (basePriceStr === null || basePriceStr === undefined) {
    return {
      label: "N/A"
    };
  }
  const basePrice = parseFloat(basePriceStr);

  // Free models
  if (basePrice === 0) {
    return {
      label: "$0",
      original: "$0"
    };
  }
  const discountNum = mapping.discount ? parseFloat(mapping.discount) : 0;

  // Request price is a flat per-request fee, not per-token
  if (field === "request") {
    const original = `$${basePrice.toFixed(3)}/req`;
    if (discountNum > 0) {
      const discountedPrice = basePrice * (1 - discountNum);
      const discounted = `$${discountedPrice.toFixed(3)}/req`;
      return {
        label: discounted,
        original,
        discounted
      };
    }
    return {
      label: original,
      original
    };
  }
  const original = formatPrice(basePrice);

  // Apply discount if present
  if (discountNum > 0) {
    const discounted = formatPrice(basePrice * (1 - discountNum));
    return {
      label: discounted,
      original,
      discounted
    };
  }
  return {
    label: original,
    original
  };
}
export interface RootAggregateInfo {
  minInputPrice?: number;
  minOutputPrice?: number;
  minCachedInputPrice?: number;
  minRequestPrice?: number;
  minImageInputPrice?: number;
  minImageOutputPrice?: number;
  maxContextSize?: number;
  maxOutput?: number;
  capabilities: string[];
}
export function getRootAggregateInfo(model: ApiModel): RootAggregateInfo {
  const now = new Date();
  let minInputPrice: number | undefined;
  let minOutputPrice: number | undefined;
  let minCachedInputPrice: number | undefined;
  let minRequestPrice: number | undefined;
  let minImageInputPrice: number | undefined;
  let minImageOutputPrice: number | undefined;
  let maxContextSize: number | undefined;
  let maxOutput: number | undefined;
  const capabilitySet = new Set<string>();
  const applyDiscount = (priceStr: string | null | undefined, discountStr?: string | null) => {
    if (priceStr === null || priceStr === undefined) {
      return undefined;
    }
    const price = parseFloat(priceStr);
    if (price === 0) {
      return 0;
    }
    const discount = discountStr ? parseFloat(discountStr) : 0;
    if (!discount || discount <= 0) {
      return price;
    }
    return price * (1 - discount);
  };
  for (const mapping of model.mappings) {
    // Skip deactivated providers when computing "best" supported values
    const isDeactivated = mapping.deactivatedAt && new Date(mapping.deactivatedAt) <= now;
    if (isDeactivated) {
      continue;
    }
    const effectiveInput = applyDiscount(mapping.inputPrice, mapping.discount);
    if (effectiveInput !== undefined && (minInputPrice === undefined || effectiveInput < minInputPrice)) {
      minInputPrice = effectiveInput;
    }
    const effectiveOutput = applyDiscount(mapping.outputPrice, mapping.discount);
    if (effectiveOutput !== undefined && (minOutputPrice === undefined || effectiveOutput < minOutputPrice)) {
      minOutputPrice = effectiveOutput;
    }
    const effectiveCached = applyDiscount(mapping.cachedInputPrice, mapping.discount);
    if (effectiveCached !== undefined && (minCachedInputPrice === undefined || effectiveCached < minCachedInputPrice)) {
      minCachedInputPrice = effectiveCached;
    }

    // Track image generation pricing
    const effectiveRequest = applyDiscount(mapping.requestPrice, mapping.discount);
    if (effectiveRequest !== undefined && (minRequestPrice === undefined || effectiveRequest < minRequestPrice)) {
      minRequestPrice = effectiveRequest;
    }
    const effectiveImageInput = applyDiscount(mapping.imageInputPrice, mapping.discount);
    if (effectiveImageInput !== undefined && (minImageInputPrice === undefined || effectiveImageInput < minImageInputPrice)) {
      minImageInputPrice = effectiveImageInput;
    }
    if (mapping.contextSize !== null && mapping.contextSize !== undefined && (maxContextSize === undefined || mapping.contextSize > maxContextSize)) {
      maxContextSize = mapping.contextSize;
    }
    if (mapping.maxOutput !== null && mapping.maxOutput !== undefined && (maxOutput === undefined || mapping.maxOutput > maxOutput)) {
      maxOutput = mapping.maxOutput;
    }
    getMappingCapabilities(mapping, model).forEach(capability => capabilitySet.add(capability));
  }

  // Ensure image generation capability is included if any provider supports image output
  if (model.output?.includes("image")) {
    capabilitySet.add("Image Generation");
  }
  return {
    minInputPrice,
    minOutputPrice,
    minCachedInputPrice,
    minRequestPrice,
    minImageInputPrice,
    minImageOutputPrice,
    maxContextSize,
    maxOutput,
    capabilities: Array.from(capabilitySet)
  };
}

// Removed old ModelItem; we render entries per provider below

export function formatPerSecondPrice(perSecondPrice: Record<string, string>): string {
  const defaultAudio = perSecondPrice["default_audio"];
  const defaultVideo = perSecondPrice["default_video"];
  const defaultPrice = perSecondPrice["default"];
  if (defaultAudio && defaultVideo) {
    return `$${defaultVideo} – $${defaultAudio}/sec`;
  }
  if (defaultPrice) {
    return `$${defaultPrice}/sec`;
  }
  const firstValue = Object.values(perSecondPrice)[0];
  return firstValue ? `$${firstValue}/sec` : "N/A";
}

// Estimate cost for generating one typical 1K/1024x1024 image based on
// request fee + image output tokens * price.
export function estimateImageCost(mapping: ApiModelProviderMapping | undefined): {
  base: number;
  discounted: number;
} | null {
  if (!mapping) {
    return null;
  }
  const request = mapping.requestPrice ? parseFloat(mapping.requestPrice) : 0;
  const discount = mapping.discount ? parseFloat(mapping.discount) : 0;
  const imageOut = mapping.imageOutputPrice ? parseFloat(mapping.imageOutputPrice) : 0;
  const tokensMap = mapping.imageOutputTokensByResolution;
  const tokens = tokensMap?.default ?? tokensMap?.["1024x1024"] ?? tokensMap?.["1K"];
  if (tokens === undefined) {
    return null;
  }
  const outputCost = imageOut * tokens;
  const base = request + outputCost;
  if (!Number.isFinite(base) || base <= 0) {
    return null;
  }
  const discounted = discount > 0 ? base * (1 - discount) : base;
  return {
    base,
    discounted
  };
}
export function getMinImageCostEstimate(mappings: ApiModelProviderMapping[]): {
  base: number;
  discounted: number;
} | null {
  const now = new Date();
  let best: {
    base: number;
    discounted: number;
  } | null = null;
  for (const m of mappings) {
    const isDeactivated = m.deactivatedAt && new Date(m.deactivatedAt) <= now;
    if (isDeactivated) {
      continue;
    }
    const est = estimateImageCost(m);
    if (!est) {
      continue;
    }
    if (!best || est.discounted < best.discounted) {
      best = est;
    }
  }
  return best;
}
export function formatImageCost(cost: number): string {
  if (cost >= 0.01) {
    return `$${cost.toFixed(3)}`;
  }
  if (cost >= 0.001) {
    return `$${cost.toFixed(4)}`;
  }
  return `$${cost.toFixed(5)}`;
}
export function ImageEstimateCard(t0) {
  const $ = _c(13);
  const {
    estimate,
    labelClassName,
    valueClassName,
    captionClassName
  } = t0;
  const t1 = labelClassName ?? "text-[11px] font-medium text-muted-foreground uppercase tracking-wide";
  let t2;
  if ($[0] !== t1) {
    t2 = <span className={t1}>Est. per Image</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = valueClassName ?? "text-sm font-mono font-semibold text-primary";
  let t4;
  if ($[2] !== estimate.discounted) {
    t4 = formatImageCost(estimate.discounted);
    $[2] = estimate.discounted;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <p className={t3}>~{t4}</p>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = captionClassName ?? "text-[10px] text-muted-foreground";
  let t7;
  if ($[7] !== t6) {
    t7 = <p className={t6}>Typical 1024×1024 output</p>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t2 || $[10] !== t5 || $[11] !== t7) {
    t8 = <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5">{t2}{t5}{t7}</div>;
    $[9] = t2;
    $[10] = t5;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
export function getMinPerSecondPrice(mappings: ApiModelProviderMapping[]): string | null {
  let min: number | null = null;
  for (const m of mappings) {
    if (!m.perSecondPrice) {
      continue;
    }
    for (const v of Object.values(m.perSecondPrice)) {
      const n = parseFloat(v);
      if (Number.isFinite(n) && (min === null || n < min)) {
        min = n;
      }
    }
  }
  return min !== null ? `$${min}/sec` : null;
}
export function getMaxPerSecondPrice(mappings: ApiModelProviderMapping[]): string | null {
  let max: number | null = null;
  for (const m of mappings) {
    if (!m.perSecondPrice) {
      continue;
    }
    for (const v of Object.values(m.perSecondPrice)) {
      const n = parseFloat(v);
      if (Number.isFinite(n) && (max === null || n > max)) {
        max = n;
      }
    }
  }
  return max !== null ? `$${max}/sec` : null;
}
export interface ModelEntry {
  model: ApiModel;
  mapping?: ApiModelProviderMapping;
  provider?: ApiProvider;
  isRoot?: boolean;
  searchText: string;
}
function normalizeSearchText(value: string): string {
  return value.toLowerCase().replace(/[-_\s]+/g, "");
}
export function createModelEntries(models: ApiModel[], providers: ApiProvider[]): ModelEntry[] {
  const out: ModelEntry[] = [];
  const now = new Date();
  const sortedModels = [...models].sort((a, b) => {
    const dateA = a.releasedAt ? new Date(a.releasedAt).getTime() : a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.releasedAt ? new Date(b.releasedAt).getTime() : b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });
  const autoModel = sortedModels.find(model => model.id === "auto");
  if (autoModel) {
    out.push({
      model: autoModel,
      isRoot: true,
      searchText: normalizeSearchText([autoModel.name ?? "", autoModel.family ?? "", autoModel.id, autoModel.aliases?.join(" ") ?? ""].join(" "))
    });
  }
  for (const model of sortedModels) {
    if (model.id === "custom" || model.id === "auto") {
      continue;
    }
    const aliasText = model.aliases?.join(" ") ?? "";
    const rootSearchText = normalizeSearchText([model.name ?? "", model.family ?? "", model.id, aliasText].join(" "));
    out.push({
      model,
      isRoot: true,
      searchText: rootSearchText
    });
    for (const mapping of model.mappings) {
      const isDeactivated = mapping.deactivatedAt && new Date(mapping.deactivatedAt) <= now;
      if (isDeactivated) {
        continue;
      }
      const provider = providers.find(p => p.id === mapping.providerId);
      const searchText = normalizeSearchText([model.name ?? "", model.family ?? "", model.id, provider?.name ?? "", aliasText].join(" "));
      out.push({
        model,
        mapping,
        provider,
        isRoot: false,
        searchText
      });
    }
  }
  return out;
}
export function getAvailableProviders(entries: ModelEntry[], providers: ApiProvider[]): ApiProvider[] {
  const ids = new Set(entries.filter(entry => entry.mapping).map(entry => entry.mapping!.providerId));
  return providers.filter(provider => ids.has(provider.id));
}
export function getAvailableCapabilities(entries: ModelEntry[]): string[] {
  const capabilities = new Set<string>();
  entries.forEach(entry => getMappingCapabilities(entry.mapping, entry.model).forEach(capability => capabilities.add(capability)));
  return Array.from(capabilities).sort();
}
export function filterModelEntries({
  entries,
  deferredSearch,
  filters,
  isFavorite,
  providersWithKeys
}: {
  entries: ModelEntry[];
  deferredSearch: string;
  filters: FilterState;
  isFavorite: (id: string) => boolean;
  providersWithKeys: Set<string>;
}): ModelEntry[] {
  let list = entries;
  if (filters.showOnlyRoot) {
    list = list.filter(entry => entry.isRoot);
  }
  if (filters.hideUnstable) {
    list = list.filter(entry => {
      if (entry.isRoot) {
        return entry.model.stability !== "unstable" && entry.model.stability !== "experimental";
      }
      return entry.mapping?.stability !== "unstable" && entry.mapping?.stability !== "experimental" && entry.model.stability !== "unstable" && entry.model.stability !== "experimental";
    });
  }
  if (deferredSearch) {
    const tokens = deferredSearch.toLowerCase().split(/[-_\s]+/).filter(Boolean);
    if (tokens.length > 0) {
      list = list.filter(entry => tokens.every(token => entry.searchText.includes(token)));
    }
  }
  if (filters.providers.length > 0) {
    list = list.filter(entry => entry.mapping && filters.providers.includes(entry.mapping.providerId));
  }
  if (filters.capabilities.length > 0) {
    list = list.filter(entry => {
      const capabilities = getMappingCapabilities(entry.mapping, entry.model);
      return filters.capabilities.every(capability => capabilities.includes(capability));
    });
  }
  if (filters.priceRange !== "all") {
    list = list.filter(entry => {
      if (!entry.mapping) {
        return false;
      }
      const price = entry.mapping.inputPrice ? parseFloat(entry.mapping.inputPrice) : 0;
      const requestPrice = entry.mapping.requestPrice ? parseFloat(entry.mapping.requestPrice) : 0;
      switch (filters.priceRange) {
        case "free":
          return entry.model.free === true && price === 0 && requestPrice === 0;
        case "low":
          return price > 0 && price <= 0.000001;
        case "medium":
          return price > 0.000001 && price <= 0.00001;
        case "high":
          return price > 0.00001;
        default:
          return true;
      }
    });
  }
  if (filters.showFavoritesOnly) {
    list = list.filter(entry => {
      if (entry.isRoot) {
        return isFavorite(entry.model.id);
      }
      const mappingId = entry.mapping ? entry.mapping.providerId + "/" + entry.model.id + (entry.mapping.region ? ":" + entry.mapping.region : "") : null;
      return mappingId !== null && isFavorite(mappingId);
    });
  }
  if (filters.showOnlyWithKeys) {
    const now = new Date();
    list = list.filter(entry => {
      if (entry.isRoot) {
        return entry.model.mappings?.some(mapping => providersWithKeys.has(mapping.providerId) && !(mapping.deactivatedAt && new Date(mapping.deactivatedAt) <= now));
      }
      return entry.mapping ? providersWithKeys.has(entry.mapping.providerId) : false;
    });
  }
  if (deferredSearch) {
    const tokens = deferredSearch.toLowerCase().split(/[-_\s]+/).filter(Boolean);
    if (tokens.length > 0) {
      list = [...list].sort((a, b) => {
        if (a.isRoot === b.isRoot) {
          return 0;
        }
        return a.isRoot ? -1 : 1;
      });
    }
  }
  return list;
}
export const ROW_HEIGHT = 56;
export interface ModelEntryRowProps {
  entries: ModelEntry[];
  focusedIndex: number;
  setFocusedIndex: (index: number) => void;
  selectedEntryKey: string;
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
  onSelect: (value: string) => void;
  setPreviewEntry: (entry: {
    model: ApiModel;
    mapping?: ApiModelProviderMapping;
    provider?: ApiProvider;
    isRoot?: boolean;
  } | null) => void;
  setSelectedDetails: (details: {
    model: ApiModel;
    mapping?: ApiModelProviderMapping;
    provider?: ApiProvider;
  } | null) => void;
  setDetailsOpen: (open: boolean) => void;
  isOptionDisabled?: (value: string) => boolean;
  getOptionDisabledReason?: (value: string) => string | undefined;
  t: ModelSelectorTranslate;
}
export function ModelEntryRowComponent(t0) {
  const $ = _c(199);
  const {
    ariaAttributes,
    index,
    style,
    entries,
    focusedIndex,
    setFocusedIndex,
    selectedEntryKey,
    isFavorite,
    toggleFavorite,
    onSelect,
    setPreviewEntry,
    setSelectedDetails,
    setDetailsOpen,
    isOptionDisabled,
    getOptionDisabledReason,
    t
  } = t0;
  const entry = entries[index];
  if (!entry) {
    return null;
  }
  const {
    model,
    mapping,
    provider,
    isRoot
  } = entry;
  const isFocused = index === focusedIndex;
  if (isRoot) {
    const entryKey = model.id;
    let t1;
    if ($[0] !== entryKey || $[1] !== isOptionDisabled) {
      t1 = isOptionDisabled?.(entryKey) ?? false;
      $[0] = entryKey;
      $[1] = isOptionDisabled;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const disabled = t1;
    let t2;
    if ($[3] !== entryKey || $[4] !== getOptionDisabledReason) {
      t2 = getOptionDisabledReason?.(entryKey);
      $[3] = entryKey;
      $[4] = getOptionDisabledReason;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const disabledReason = t2;
    let t3;
    if ($[6] !== model.mappings) {
      t3 = model.mappings.some(_temp);
      $[6] = model.mappings;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const hasRequestPrice = t3;
    let t4;
    if ($[8] !== hasRequestPrice || $[9] !== model.free || $[10] !== model.mappings) {
      t4 = model.free === true && !hasRequestPrice && model.mappings.every(_temp2);
      $[8] = hasRequestPrice;
      $[9] = model.free;
      $[10] = model.mappings;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    const isFreeRoot = t4;
    let t5;
    if ($[12] !== index || $[13] !== isRoot || $[14] !== mapping || $[15] !== model || $[16] !== provider || $[17] !== setFocusedIndex || $[18] !== setPreviewEntry) {
      t5 = () => {
        setFocusedIndex(index);
        setPreviewEntry({
          model,
          mapping,
          provider,
          isRoot
        });
      };
      $[12] = index;
      $[13] = isRoot;
      $[14] = mapping;
      $[15] = model;
      $[16] = provider;
      $[17] = setFocusedIndex;
      $[18] = setPreviewEntry;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[20] !== disabled || $[21] !== model.id || $[22] !== onSelect) {
      t6 = () => {
        if (disabled) {
          return;
        }
        onSelect(model.id);
      };
      $[20] = disabled;
      $[21] = model.id;
      $[22] = onSelect;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    const t7 = isFocused ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground";
    const t8 = disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer";
    let t9;
    if ($[24] !== t7 || $[25] !== t8) {
      t9 = cn("relative flex h-full select-none items-center gap-2 rounded-sm px-2 text-sm outline-none", t7, t8);
      $[24] = t7;
      $[25] = t8;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    const t10 = entryKey === selectedEntryKey ? "opacity-100" : "opacity-0";
    let t11;
    if ($[27] !== t10) {
      t11 = cn("h-4 w-4 shrink-0", t10);
      $[27] = t10;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== t11) {
      t12 = <Check className={t11} />;
      $[29] = t11;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    let t13;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <Sparkles className="h-6 w-6 shrink-0 text-primary" />;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    let t14;
    if ($[32] !== model.name) {
      t14 = <span className="font-medium truncate">{model.name}</span>;
      $[32] = model.name;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] !== isFreeRoot) {
      t15 = isFreeRoot && <Gift className="h-3.5 w-3.5 shrink-0 text-emerald-500" />;
      $[34] = isFreeRoot;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    let t16;
    if ($[36] !== t14 || $[37] !== t15) {
      t16 = <div className="flex items-center gap-1 min-w-0">{t14}{t15}</div>;
      $[36] = t14;
      $[37] = t15;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    let t17;
    if ($[39] !== t) {
      t17 = t("modelSelector.autoSelectProvider");
      $[39] = t;
      $[40] = t17;
    } else {
      t17 = $[40];
    }
    let t18;
    if ($[41] !== t17) {
      t18 = <span className="text-xs text-muted-foreground truncate">{t17}</span>;
      $[41] = t17;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    let t19;
    if ($[43] !== t16 || $[44] !== t18) {
      t19 = <div className="flex items-center gap-2 min-w-0 flex-1">{t13}<div className="flex flex-col min-w-0 flex-1">{t16}{t18}</div></div>;
      $[43] = t16;
      $[44] = t18;
      $[45] = t19;
    } else {
      t19 = $[45];
    }
    let t20;
    if ($[46] !== isFavorite || $[47] !== model.id || $[48] !== t) {
      t20 = isFavorite(model.id) ? t("modelSelector.removeFromFavorites") : t("modelSelector.addToFavorites");
      $[46] = isFavorite;
      $[47] = model.id;
      $[48] = t;
      $[49] = t20;
    } else {
      t20 = $[49];
    }
    let t21;
    if ($[50] !== isFavorite || $[51] !== model.id) {
      t21 = isFavorite(model.id);
      $[50] = isFavorite;
      $[51] = model.id;
      $[52] = t21;
    } else {
      t21 = $[52];
    }
    let t22;
    if ($[53] !== model.id || $[54] !== toggleFavorite) {
      t22 = e => {
        e.stopPropagation();
        toggleFavorite(model.id);
      };
      $[53] = model.id;
      $[54] = toggleFavorite;
      $[55] = t22;
    } else {
      t22 = $[55];
    }
    let t23;
    if ($[56] !== isFavorite || $[57] !== model.id) {
      t23 = isFavorite(model.id) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground";
      $[56] = isFavorite;
      $[57] = model.id;
      $[58] = t23;
    } else {
      t23 = $[58];
    }
    let t24;
    if ($[59] !== t23) {
      t24 = cn("h-4 w-4", t23);
      $[59] = t23;
      $[60] = t24;
    } else {
      t24 = $[60];
    }
    let t25;
    if ($[61] !== t24) {
      t25 = <Star className={t24} />;
      $[61] = t24;
      $[62] = t25;
    } else {
      t25 = $[62];
    }
    let t26;
    if ($[63] !== t20 || $[64] !== t21 || $[65] !== t22 || $[66] !== t25) {
      t26 = <Button variant="ghost" size="sm" aria-label={t20} aria-pressed={t21} className="h-8 w-8 p-0 hover:bg-muted/50 shrink-0" onClick={t22}>{t25}</Button>;
      $[63] = t20;
      $[64] = t21;
      $[65] = t22;
      $[66] = t25;
      $[67] = t26;
    } else {
      t26 = $[67];
    }
    let t27;
    if ($[68] !== t) {
      t27 = t("modelSelector.modelDetails");
      $[68] = t;
      $[69] = t27;
    } else {
      t27 = $[69];
    }
    let t28;
    if ($[70] !== model || $[71] !== setDetailsOpen || $[72] !== setSelectedDetails) {
      t28 = e_0 => {
        e_0.stopPropagation();
        setSelectedDetails({
          model
        });
        setDetailsOpen(true);
      };
      $[70] = model;
      $[71] = setDetailsOpen;
      $[72] = setSelectedDetails;
      $[73] = t28;
    } else {
      t28 = $[73];
    }
    let t29;
    if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <Info className="h-4 w-4" />;
      $[74] = t29;
    } else {
      t29 = $[74];
    }
    let t30;
    if ($[75] !== t27 || $[76] !== t28) {
      t30 = <Button variant="ghost" size="sm" aria-label={t27} className="h-8 w-8 p-0 hover:bg-muted/50 shrink-0 md:hidden" onClick={t28}>{t29}</Button>;
      $[75] = t27;
      $[76] = t28;
      $[77] = t30;
    } else {
      t30 = $[77];
    }
    let t31;
    if ($[78] !== t19 || $[79] !== t26 || $[80] !== t30) {
      t31 = <div className="flex items-center justify-between flex-1 min-w-0 gap-2">{t19}{t26}{t30}</div>;
      $[78] = t19;
      $[79] = t26;
      $[80] = t30;
      $[81] = t31;
    } else {
      t31 = $[81];
    }
    let t32;
    if ($[82] !== disabledReason || $[83] !== t12 || $[84] !== t31 || $[85] !== t5 || $[86] !== t6 || $[87] !== t9) {
      t32 = <div title={disabledReason} onMouseEnter={t5} onClick={t6} className={t9}>{t12}{t31}</div>;
      $[82] = disabledReason;
      $[83] = t12;
      $[84] = t31;
      $[85] = t5;
      $[86] = t6;
      $[87] = t9;
      $[88] = t32;
    } else {
      t32 = $[88];
    }
    let t33;
    if ($[89] !== ariaAttributes || $[90] !== style || $[91] !== t32) {
      t33 = <div {...ariaAttributes} style={style} className="px-1 py-0.5">{t32}</div>;
      $[89] = ariaAttributes;
      $[90] = style;
      $[91] = t32;
      $[92] = t33;
    } else {
      t33 = $[92];
    }
    return t33;
  }
  let t1;
  if ($[93] !== provider) {
    t1 = provider ? getProviderIcon(provider.id) : null;
    $[93] = provider;
    $[94] = t1;
  } else {
    t1 = $[94];
  }
  const ProviderIcon = t1;
  const entryKey_0 = `${mapping.providerId}-${model.id}-${mapping.region ?? ""}`;
  const providerModelValue = `${mapping.providerId}/${model.id}${mapping.region ? `:${mapping.region}` : ""}`;
  let t2;
  if ($[95] !== isOptionDisabled || $[96] !== providerModelValue) {
    t2 = isOptionDisabled?.(providerModelValue) ?? false;
    $[95] = isOptionDisabled;
    $[96] = providerModelValue;
    $[97] = t2;
  } else {
    t2 = $[97];
  }
  const disabled_0 = t2;
  let t3;
  if ($[98] !== getOptionDisabledReason || $[99] !== providerModelValue) {
    t3 = getOptionDisabledReason?.(providerModelValue);
    $[98] = getOptionDisabledReason;
    $[99] = providerModelValue;
    $[100] = t3;
  } else {
    t3 = $[100];
  }
  const disabledReason_0 = t3;
  let t4;
  if ($[101] !== mapping || $[102] !== model) {
    t4 = isModelUnstable(mapping, model);
    $[101] = mapping;
    $[102] = model;
    $[103] = t4;
  } else {
    t4 = $[103];
  }
  const isUnstable = t4;
  let t5;
  if ($[104] !== mapping.deprecatedAt) {
    t5 = mapping.deprecatedAt && new Date(mapping.deprecatedAt) <= new Date();
    $[104] = mapping.deprecatedAt;
    $[105] = t5;
  } else {
    t5 = $[105];
  }
  const isDeprecated = t5;
  const hasRequestPrice_0 = mapping.requestPrice && parseFloat(mapping.requestPrice) > 0;
  const isFreeMapping = model.free === true && !hasRequestPrice_0 && (!mapping.inputPrice || parseFloat(mapping.inputPrice) === 0) && (!mapping.outputPrice || parseFloat(mapping.outputPrice) === 0);
  let t6;
  if ($[106] !== index || $[107] !== isRoot || $[108] !== mapping || $[109] !== model || $[110] !== provider || $[111] !== setFocusedIndex || $[112] !== setPreviewEntry) {
    t6 = () => {
      setFocusedIndex(index);
      setPreviewEntry({
        model,
        mapping,
        provider,
        isRoot
      });
    };
    $[106] = index;
    $[107] = isRoot;
    $[108] = mapping;
    $[109] = model;
    $[110] = provider;
    $[111] = setFocusedIndex;
    $[112] = setPreviewEntry;
    $[113] = t6;
  } else {
    t6 = $[113];
  }
  let t7;
  if ($[114] !== disabled_0 || $[115] !== onSelect || $[116] !== providerModelValue) {
    t7 = () => {
      if (disabled_0) {
        return;
      }
      onSelect(providerModelValue);
    };
    $[114] = disabled_0;
    $[115] = onSelect;
    $[116] = providerModelValue;
    $[117] = t7;
  } else {
    t7 = $[117];
  }
  const t8 = isFocused ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground";
  const t9 = disabled_0 ? "cursor-not-allowed opacity-50" : "cursor-pointer";
  let t10;
  if ($[118] !== t8 || $[119] !== t9) {
    t10 = cn("relative flex h-full select-none items-center gap-2 rounded-sm px-2 text-sm outline-none", t8, t9);
    $[118] = t8;
    $[119] = t9;
    $[120] = t10;
  } else {
    t10 = $[120];
  }
  const t11 = entryKey_0 === selectedEntryKey ? "opacity-100" : "opacity-0";
  let t12;
  if ($[121] !== t11) {
    t12 = cn("h-4 w-4 shrink-0", t11);
    $[121] = t11;
    $[122] = t12;
  } else {
    t12 = $[122];
  }
  let t13;
  if ($[123] !== t12) {
    t13 = <Check className={t12} />;
    $[123] = t12;
    $[124] = t13;
  } else {
    t13 = $[124];
  }
  let t14;
  if ($[125] !== ProviderIcon) {
    t14 = ProviderIcon ? <ProviderIcon className="h-6 w-6 shrink-0 dark:text-white" /> : null;
    $[125] = ProviderIcon;
    $[126] = t14;
  } else {
    t14 = $[126];
  }
  let t15;
  if ($[127] !== model.name) {
    t15 = <span className="font-medium truncate">{model.name}</span>;
    $[127] = model.name;
    $[128] = t15;
  } else {
    t15 = $[128];
  }
  let t16;
  if ($[129] !== isFreeMapping) {
    t16 = isFreeMapping && <Gift className="h-3.5 w-3.5 shrink-0 text-emerald-500" />;
    $[129] = isFreeMapping;
    $[130] = t16;
  } else {
    t16 = $[130];
  }
  let t17;
  if ($[131] !== isDeprecated || $[132] !== isUnstable) {
    t17 = (isUnstable || isDeprecated) && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-yellow-600 dark:text-yellow-500" />;
    $[131] = isDeprecated;
    $[132] = isUnstable;
    $[133] = t17;
  } else {
    t17 = $[133];
  }
  let t18;
  if ($[134] !== t15 || $[135] !== t16 || $[136] !== t17) {
    t18 = <div className="flex items-center gap-1 min-w-0">{t15}{t16}{t17}</div>;
    $[134] = t15;
    $[135] = t16;
    $[136] = t17;
    $[137] = t18;
  } else {
    t18 = $[137];
  }
  const t19 = disabledReason_0 ?? provider?.name;
  let t20;
  if ($[138] !== disabledReason_0 || $[139] !== mapping.region) {
    t20 = !disabledReason_0 && mapping?.region && <span className="ml-1">({mapping.region})</span>;
    $[138] = disabledReason_0;
    $[139] = mapping.region;
    $[140] = t20;
  } else {
    t20 = $[140];
  }
  let t21;
  if ($[141] !== t19 || $[142] !== t20) {
    t21 = <span className="text-xs text-muted-foreground truncate">{t19}{t20}</span>;
    $[141] = t19;
    $[142] = t20;
    $[143] = t21;
  } else {
    t21 = $[143];
  }
  let t22;
  if ($[144] !== t18 || $[145] !== t21) {
    t22 = <div className="flex flex-col min-w-0 flex-1">{t18}{t21}</div>;
    $[144] = t18;
    $[145] = t21;
    $[146] = t22;
  } else {
    t22 = $[146];
  }
  let t23;
  if ($[147] !== t14 || $[148] !== t22) {
    t23 = <div className="flex items-center gap-2 min-w-0 flex-1">{t14}{t22}</div>;
    $[147] = t14;
    $[148] = t22;
    $[149] = t23;
  } else {
    t23 = $[149];
  }
  let t24;
  if ($[150] !== isFavorite || $[151] !== providerModelValue || $[152] !== t) {
    t24 = isFavorite(providerModelValue) ? t("modelSelector.removeFromFavorites") : t("modelSelector.addToFavorites");
    $[150] = isFavorite;
    $[151] = providerModelValue;
    $[152] = t;
    $[153] = t24;
  } else {
    t24 = $[153];
  }
  let t25;
  if ($[154] !== isFavorite || $[155] !== providerModelValue) {
    t25 = isFavorite(providerModelValue);
    $[154] = isFavorite;
    $[155] = providerModelValue;
    $[156] = t25;
  } else {
    t25 = $[156];
  }
  let t26;
  if ($[157] !== providerModelValue || $[158] !== toggleFavorite) {
    t26 = e_1 => {
      e_1.stopPropagation();
      toggleFavorite(providerModelValue);
    };
    $[157] = providerModelValue;
    $[158] = toggleFavorite;
    $[159] = t26;
  } else {
    t26 = $[159];
  }
  let t27;
  if ($[160] !== isFavorite || $[161] !== providerModelValue) {
    t27 = isFavorite(providerModelValue) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground";
    $[160] = isFavorite;
    $[161] = providerModelValue;
    $[162] = t27;
  } else {
    t27 = $[162];
  }
  let t28;
  if ($[163] !== t27) {
    t28 = cn("h-4 w-4", t27);
    $[163] = t27;
    $[164] = t28;
  } else {
    t28 = $[164];
  }
  let t29;
  if ($[165] !== t28) {
    t29 = <Star className={t28} />;
    $[165] = t28;
    $[166] = t29;
  } else {
    t29 = $[166];
  }
  let t30;
  if ($[167] !== t24 || $[168] !== t25 || $[169] !== t26 || $[170] !== t29) {
    t30 = <Button variant="ghost" size="sm" aria-label={t24} aria-pressed={t25} className="h-8 w-8 p-0 hover:bg-muted/50 shrink-0" onClick={t26}>{t29}</Button>;
    $[167] = t24;
    $[168] = t25;
    $[169] = t26;
    $[170] = t29;
    $[171] = t30;
  } else {
    t30 = $[171];
  }
  let t31;
  if ($[172] !== t) {
    t31 = t("modelSelector.modelDetails");
    $[172] = t;
    $[173] = t31;
  } else {
    t31 = $[173];
  }
  let t32;
  if ($[174] !== mapping || $[175] !== model || $[176] !== provider || $[177] !== setDetailsOpen || $[178] !== setSelectedDetails) {
    t32 = e_2 => {
      e_2.stopPropagation();
      setSelectedDetails({
        model,
        mapping,
        provider
      });
      setDetailsOpen(true);
    };
    $[174] = mapping;
    $[175] = model;
    $[176] = provider;
    $[177] = setDetailsOpen;
    $[178] = setSelectedDetails;
    $[179] = t32;
  } else {
    t32 = $[179];
  }
  let t33;
  if ($[180] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Info className="h-4 w-4" />;
    $[180] = t33;
  } else {
    t33 = $[180];
  }
  let t34;
  if ($[181] !== t31 || $[182] !== t32) {
    t34 = <Button variant="ghost" size="sm" aria-label={t31} className="h-8 w-8 p-0 hover:bg-muted/50 shrink-0 md:hidden" onClick={t32}>{t33}</Button>;
    $[181] = t31;
    $[182] = t32;
    $[183] = t34;
  } else {
    t34 = $[183];
  }
  let t35;
  if ($[184] !== t23 || $[185] !== t30 || $[186] !== t34) {
    t35 = <div className="flex items-center justify-between flex-1 min-w-0 gap-2">{t23}{t30}{t34}</div>;
    $[184] = t23;
    $[185] = t30;
    $[186] = t34;
    $[187] = t35;
  } else {
    t35 = $[187];
  }
  let t36;
  if ($[188] !== disabledReason_0 || $[189] !== t10 || $[190] !== t13 || $[191] !== t35 || $[192] !== t6 || $[193] !== t7) {
    t36 = <div title={disabledReason_0} onMouseEnter={t6} onClick={t7} className={t10}>{t13}{t35}</div>;
    $[188] = disabledReason_0;
    $[189] = t10;
    $[190] = t13;
    $[191] = t35;
    $[192] = t6;
    $[193] = t7;
    $[194] = t36;
  } else {
    t36 = $[194];
  }
  let t37;
  if ($[195] !== ariaAttributes || $[196] !== style || $[197] !== t36) {
    t37 = <div {...ariaAttributes} style={style} className="px-1 py-0.5">{t36}</div>;
    $[195] = ariaAttributes;
    $[196] = style;
    $[197] = t36;
    $[198] = t37;
  } else {
    t37 = $[198];
  }
  return t37;
}
function _temp2(p_0) {
  return (!p_0.inputPrice || parseFloat(p_0.inputPrice) === 0) && (!p_0.outputPrice || parseFloat(p_0.outputPrice) === 0);
}
function _temp(p) {
  return p.requestPrice && parseFloat(p.requestPrice) > 0;
}